import { useCallback, useEffect, useState } from "react";
import { ChartLineUp, Eye, UserPlus, CheckCircle } from "@phosphor-icons/react";
import API from "../../lib/api";

const RANGES = [[7, "7 days"], [30, "30 days"], [90, "90 days"], [365, "12 months"]];

function Step({ icon: Icon, label, value, sub, testId }) {
  return (
    <div className="rounded-lg border border-border bg-muted/30 px-3 py-2 min-w-0" data-testid={testId}>
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1"><Icon size={12} /> {label}</p>
      <p className="text-xl font-bold font-[Chivo] leading-tight">{value}</p>
      {sub && <p className="text-[10px] text-muted-foreground">{sub}</p>}
    </div>
  );
}

function Sparkline({ points }) {
  /* Long ranges are bucketed by week so the bars never overflow the card. */
  const bucket = points.length > 60 ? 7 : 1;
  const bars = [];
  for (let i = 0; i < points.length; i += bucket) {
    const slice = points.slice(i, i + bucket);
    bars.push({ day: slice[0].day, to: slice[slice.length - 1].day, views: slice.reduce((n, p) => n + p.views, 0) });
  }
  const max = Math.max(1, ...bars.map((b) => b.views));
  return (
    <div className="flex items-end gap-[2px] h-10 w-full overflow-hidden" data-testid="link-sparkline" title={bucket > 1 ? "Partner-page views per week" : "Partner-page views per day"}>
      {bars.map((b) => (
        <div key={b.day} className="flex-1 rounded-sm bg-primary/70 min-w-0" style={{ height: `${Math.max(4, (b.views / max) * 100)}%`, opacity: b.views ? 1 : 0.25 }}
          title={`${b.day}${bucket > 1 ? ` – ${b.to}` : ""}: ${b.views} view${b.views === 1 ? "" : "s"}`} />
      ))}
    </div>
  );
}

/* Partner-link funnel: page views → sign-ups started → tenants created → live. `endpoint` is the admin or portal stats URL. */
export function LinkFunnel({ endpoint, compact = false }) {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const load = useCallback(async () => {
    try { const { data } = await API.get(endpoint, { params: { days } }); setData(data); } catch { setData({ error: true }); }
  }, [endpoint, days]);
  useEffect(() => { load(); }, [load]);
  const t = data?.totals;
  return (
    <section className="rounded-xl border border-border bg-card p-4 space-y-3" data-testid="link-funnel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold flex items-center gap-1.5"><ChartLineUp size={16} /> Partner links</h3>
          <p className="text-[11px] text-muted-foreground">Who opened the partner page, who started sign-up and who became a tenant.</p>
        </div>
        <select value={days} onChange={(e) => setDays(Number(e.target.value))} className="h-8 rounded-md border border-input bg-background px-2 text-xs" data-testid="link-range">
          {RANGES.map(([d, l]) => <option key={d} value={d}>Last {l}</option>)}
        </select>
      </div>
      {!data && <p className="text-xs text-muted-foreground">Loading…</p>}
      {data?.error && <p className="text-xs text-red-600">Couldn't load link stats</p>}
      {t && (
        <>
          <div className="grid gap-2 grid-cols-2 lg:grid-cols-4">
            <Step icon={Eye} label="Page views" value={t.views} sub={`${t.views_all} all time`} testId="funnel-views" />
            <Step icon={UserPlus} label="Sign-ups started" value={t.signup_starts} sub={t.views ? `${Math.round((t.signup_starts / t.views) * 100)}% of views` : `${t.signup_starts_all} all time`} testId="funnel-starts" />
            <Step icon={CheckCircle} label="Tenants created" value={t.signups} sub={t.signup_starts ? `${Math.round((t.signups / t.signup_starts) * 100)}% of starts` : `${t.signups_all} all time`} testId="funnel-signups" />
            <Step icon={CheckCircle} label="Live now" value={t.live} sub={`${t.tenants_all} tenant${t.tenants_all === 1 ? "" : "s"} via your codes`} testId="funnel-live" />
          </div>
          <Sparkline points={data.daily_views} />
          {!compact && data.codes.length > 0 && (
            <table className="w-full text-xs" data-testid="funnel-codes">
              <thead className="text-[10px] uppercase tracking-wider text-muted-foreground"><tr><th className="text-left py-1">Code</th><th className="text-right py-1">Views</th><th className="text-right py-1">Started</th><th className="text-right py-1">Signed up</th><th className="text-right py-1">Live</th></tr></thead>
              <tbody>
                {data.codes.map((c) => (
                  <tr key={c.code} className="border-t border-border" data-testid={`funnel-code-${c.code}`}>
                    <td className="py-1.5 font-mono font-semibold">{c.code} <span className="font-sans font-normal text-muted-foreground">{c.discount_pct}%{c.active ? "" : " · off"}</span></td>
                    <td className="py-1.5 text-right">{c.views}</td><td className="py-1.5 text-right">{c.signup_starts}</td><td className="py-1.5 text-right">{c.signups}</td><td className="py-1.5 text-right">{c.live}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {t.views_all === 0 && <p className="text-[11px] text-muted-foreground" data-testid="funnel-empty">No visits yet — share your partner link to start seeing numbers here.</p>}
        </>
      )}
    </section>
  );
}
